import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/ui/Alert';

import { defiYieldsOverviewBunja } from '../../dynamic-earn/shared/state';
import { useIsWebview } from '../../shared/webview';
import CollateralValueRatioBottomSheet from './collateral-value-ratio-bottom-sheet';

const SupportedAssets = () => {
  const t = useTranslations();
  const overlay = useOverlay();
  const isWebview = useIsWebview();
  const { nrsusdtpAvailableAtom } = useBunja(defiYieldsOverviewBunja);
  const nrsusdtpAvailable = useAtomValue(nrsusdtpAvailableAtom);

  const rows = useMemo(() => {
    const base = [
      { name: 'BTC', ratio: 0.95 },
      { name: 'ETH', ratio: 0.95 },
    ];
    if (nrsusdtpAvailable) base.push({ name: 'Narsil USDT Prime', ratio: 0.85 });
    base.push(
      { name: 'USDe', ratio: 0.99 },
      { name: 'USDC', ratio: 0.99 },
      { name: 'USDT', ratio: 1 },
      { name: 'USDT in Premium Earn', ratio: 1 },
      { name: 'USDT Bonus', ratio: 1 },
    );
    return base;
  }, [nrsusdtpAvailable]);

  return (
    <div className={flex({ flex: 1, direction: 'column', gap: 16 })}>
      <h2 className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Multi_asset_collateral_supported_assets' />
      </h2>
      <table
        className={css({
          w: '100%',
          borderCollapse: 'collapse',
          borderStyle: 'hidden',
          '& th': {
            textStyle: 'Text/S/Regular',
            color: 'content.default.level-1',
            pt: 16,
            pb: 12,
          },
          '& td': {
            py: 16,
          },
          '& th, & td': {
            borderBottomWidth: 1,
            borderStyle: 'solid',
            borderColor: 'border.default.level-1',
          },
          '& th:first-child, & td:first-child': {
            textAlign: 'left',
          },
          '& th:last-child, & td:last-child': {
            textAlign: 'right',
          },
          '& a': {
            color: 'content.accent.info',
            textDecoration: 'underline',
          },
        })}
      >
        <thead>
          <tr>
            <th>
              <FormattedMessage id='Autogen_common.r251126.s_Name' />
            </th>
            <th>
              <div className={flex({ gap: 4, justify: 'flex-end', align: 'center' })}>
                Collateral value ratio
                <button
                  type='button'
                  className={flex()}
                  onClick={() => {
                    if (isWebview) {
                      overlay.open(({ isOpen, onClose, unmount }) => (
                        <CollateralValueRatioBottomSheet isOpen={isOpen} onClose={onClose} onExit={unmount} />
                      ));
                    } else {
                      alert.show({
                        title: 'Collateral value ratio',
                        message:
                          'The collateral value ratio shows how much of an asset is recognized as collateral.\n' +
                          'For example, if BTC has a ratio of 95%, then 95 USDT out of 100 USDT worth of BTC is counted as collateral.',
                        confirmText: t('Common.r250401.Ok'),
                      });
                    }
                  }}
                >
                  <InformationCircleIcon size={14} color={token('colors.content.default.level-1')} />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ name, ratio }) => (
            <tr key={name}>
              <td>{name}</td>
              <td>
                <Formatted.Percent value={ratio} isRate />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SupportedAssets;
